import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    // On article pages, immediately set scrolled state for visible navbar
    if (!isHome) {
      setScrolled(true);
    }
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHome]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const closeAll = () => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
  };

  // Determine navbar style based on state
  const isTransparent = isHome && !scrolled;
  const navBg = isTransparent
    ? "bg-transparent"
    : "bg-background-50/95 backdrop-blur-md";
  const textColor = isTransparent
    ? "text-white/90 hover:text-white"
    : "text-foreground-700 hover:text-foreground-950";
  const brandColor = isTransparent
    ? "text-white"
    : "text-foreground-950";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Left: Nav Links */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            {/* Home */}
            <Link
              to="/"
              onClick={closeAll}
              className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer ${textColor}`}
            >
              Home
            </Link>

            {/* Spreadsheet */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer ${textColor}`}
            >
              Spreadsheet
            </a>

            {/* Categories Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1 text-sm font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer ${textColor}`}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-60 bg-background-50 rounded-lg overflow-hidden max-h-80 overflow-y-auto shadow-lg">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={closeAll}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 whitespace-nowrap border-b border-background-100 last:border-b-0"
                    >
                      <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-100">
                        <i className="ri-arrow-right-up-line text-xs text-foreground-500"></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Shopping Guide */}
            <Link
              to="/blog/pandabuy-spreadsheet-buying-guide"
              onClick={closeAll}
              className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer ${textColor}`}
            >
              Shopping Guide
            </Link>
          </div>

          {/* Center: Brand */}
          <div className="flex items-center">
            <Link
              to="/"
              className={`font-heading text-xl md:text-2xl font-bold tracking-wide whitespace-nowrap transition-colors duration-200 ${brandColor}`}
            >
              pandabuy<span className="font-light">.run</span>
            </Link>
          </div>

          {/* Right: CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-semibold whitespace-nowrap rounded-md bg-white text-foreground-900 hover:bg-background-100 transition-all duration-200 cursor-pointer border border-background-200"
            >
              Pandabuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-semibold whitespace-nowrap rounded-md bg-primary-500 text-white hover:bg-primary-600 transition-all duration-200 cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden flex items-center justify-center w-10 h-10 cursor-pointer ${brandColor}`}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 flex flex-col gap-1">
            {/* Home */}
            <Link
              to="/"
              onClick={closeAll}
              className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100">
                <i className="ri-home-4-line text-primary-600 text-sm"></i>
              </span>
              Home
            </Link>

            {/* Spreadsheet */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeAll}
              className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100">
                <i className="ri-external-link-line text-accent-600 text-sm"></i>
              </span>
              Spreadsheet
            </a>

            {/* Shopping Guide */}
            <Link
              to="/blog/pandabuy-spreadsheet-buying-guide"
              onClick={closeAll}
              className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100">
                <i className="ri-book-open-line text-secondary-600 text-sm"></i>
              </span>
              Shopping Guide
            </Link>

            {/* Mobile Categories Accordion */}
            <div className="border-t border-background-200 mt-2 pt-2">
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="flex items-center justify-between w-full px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors"
              >
                <span className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200">
                    <i className="ri-grid-line text-foreground-600 text-sm"></i>
                  </span>
                  Categories ({categories.length})
                </span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`}></i>
              </button>

              {mobileCatOpen && (
                <div className="grid grid-cols-2 gap-1 px-2 mt-1 pb-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={closeAll}
                      className="flex items-center gap-2 px-3 py-2.5 text-xs font-medium text-foreground-600 hover:bg-background-100 hover:text-foreground-900 rounded-md transition-colors"
                    >
                      <i className="ri-arrow-right-up-line text-[10px] text-foreground-400"></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-background-200">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeAll}
                className="px-4 py-3 text-sm font-semibold text-center rounded-lg bg-foreground-900 text-white hover:bg-foreground-800 transition-colors cursor-pointer"
              >
                Pandabuy Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeAll}
                className="px-4 py-3 text-sm font-semibold text-center rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}